// --- Dependencies -------------------------------------------------------------------------------
import type { KeyboardEvent } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    value: string;
    onChange: (value: string) => void;

    /** Restores the last saved value for this cell — bound to Escape. */
    onRevert?: () => void;

    /** Escape on a row that is still completely blank abandons it instead of reverting. */
    onEscapeEmpty?: () => void;

    /** Enter on the last row — appends a row and lands on it, so a menu is typed without reaching for the mouse. */
    onEnterPastEnd?: () => void;

    column: string;
    rowIndex: number;
    dirty?: boolean;
    dir?: "rtl" | "ltr";
    numeric?: boolean;
    placeholder?: string;
    disabled?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One spreadsheet cell. The input is always live rather than a click-to-edit state: on a phone an
 * extra tap before every edit is the difference between entering a menu quickly and giving up.
 *
 * Enter walks down the column and Escape undoes the cell, which is what anyone who has used a
 * spreadsheet already expects. Tab is left to the browser — its natural order is row by row.
 *
 * Enter on the last row grows the grid instead of stopping dead, so a whole menu is typed top to
 * bottom without ever reaching for the "add row" button.
 */
export default function Cell({
    value,
    onChange,
    onRevert,
    onEscapeEmpty,
    onEnterPastEnd,
    column,
    rowIndex,
    dirty,
    dir,
    numeric,
    placeholder,
    disabled,
}: Props) {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.currentTarget.blur();
            // A blank row the user escaped out of was a misfire — take it away rather than leave litter.
            if (onEscapeEmpty) onEscapeEmpty();
            else onRevert?.();
            return;
        }

        if (event.key !== "Enter") return;
        event.preventDefault();
        const step = event.shiftKey ? -1 : 1;
        const target = document.querySelector<HTMLInputElement>(`[data-cell-column="${column}"][data-cell-row="${rowIndex + step}"]`);

        if (target) {
            target.focus();
            target.select();
            return;
        }

        // Past the last row, Enter grows the grid. Shift+Enter at the top has nowhere to go, and a
        // row appended above where the user is typing would be worse than doing nothing.
        if (step === 1) onEnterPastEnd?.();
    };

    return (
        <input
            className={`editor-cell${dirty ? " editor-cell--dirty" : ""}`}
            type={numeric ? "number" : "text"}
            inputMode={numeric ? "decimal" : undefined}
            dir={dir}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            data-cell-column={column}
            data-cell-row={rowIndex}
            autoComplete="off"
        />
    );
}
